import type { SpectatorGuide } from '../types';

// From context-chicago/compass_artifact_wf-894adff9-…_text_markdown.md (spectator spots, transit, strategy).
// Mile markers shift slightly year to year; the 2026 course map is the authority.
export const chicagoSpectators: SpectatorGuide = {
  lede: 'For supporters. Ride the CTA ‘L’ north to south and you can see your runner 3–4 times without fighting the finish crowds, then meet at Runner Reunite in Grant Park. Spectators can’t enter the start or finish areas.',
  startCrossingMin: 10,
  spots: [
    {
      mile: 1,
      name: 'Grand Avenue',
      where: 'Grand Ave between Columbus Dr and State St · Streeterville',
      transit: 'Grand (Red)',
      why: 'The first spot outside Grant Park. Good for the wheelchair racers and elites.',
      downside: 'Very dense — locals warn it can be too packed to pick out your runner.',
    },
    {
      mile: 2,
      name: 'The Loop',
      where: 'Dearborn St (moved from State St for 2026, per The Running Channel) · Jackson Blvd',
      transit: 'Jackson (Blue, on Dearborn) or Jackson (Red)',
      why: 'Skyscraper backdrop and big crowds, close to the hotels.',
      downside: 'The 2026 move to Dearborn rests on one source, so check the official course map. Crowds are deep.',
    },
    {
      mile: 3.5,
      name: 'River North · Chicago & LaSalle',
      where: 'Chicago Ave & LaSalle St — stand on the east side',
      transit: 'Chicago (Red), not the Brown Line, which makes you cross the course',
      why: 'A quick first sighting with lots of energy. Wells St (miles 12–12.5) is one block west, so you can see your runner twice here.',
    },
    {
      mile: 5,
      name: 'Lincoln Park · 8K',
      where: 'Stockton Dr near Wisconsin St',
      transit: 'Sedgwick (Brown), then a ~15-min walk north-east',
      why: 'Roomy, green and family-friendly. Wear green.',
      downside: 'The longest walk from a station, and runners are still bunched in packs this early.',
      cheerZone: 'Shamrock Shuffle Cheer Zone',
    },
    {
      mile: 8,
      name: 'Northalsted (Boystown)',
      where: 'Broadway between Sheridan and Belmont · Sheridan & Belmont is mile 7, Broadway & Belmont is mile 9',
      transit: 'Addison or Sheridan (Red) · Belmont (Red/Brown/Purple)',
      why: 'Widely rated the best atmosphere on the course: blasting music and a drag stage. The 6-minute walk between miles 7 and 9 makes an easy double sighting.',
      downside: 'One of the most crowded stretches, so arrive early. Doing the double means crossing the course to get back to the train.',
    },
    {
      mile: 11,
      name: 'Old Town · North & Sedgwick',
      where: 'North Ave & Sedgwick St — stand on the west side',
      transit: 'Sedgwick (Brown), half a block north',
      why: 'Crowds thin out a bit, so the view is good, and there are cafés on Wells. The Endorphins cheer zone is here too.',
    },
    {
      mile: 13,
      name: 'North Wacker · halfway',
      where: 'N Wacker Dr outside Bank of America Tower; Chicago 13.1 zone near Wacker & Washington',
      transit: 'Washington/Wells (Brown/Pink/Orange), then two blocks west',
      why: 'Central, easy to reach, and halfway — with entertainment and cheer items.',
      cheerZone: 'Bank of America Cheer Zone',
    },
    {
      mile: 15,
      name: 'Greektown · Charity Block Party',
      where: 'Adams St & Loomis St, near Whitney Young High School',
      transit: 'Racine (Blue)',
      why: 'The place to cheer a charity runner. You can walk on to mile 17 afterwards.',
      cheerZone: 'Charity Block Party',
    },
    {
      mile: 17,
      name: 'UIC / Little Italy',
      where: 'Taylor St and S Halsted — the race director’s pick if you only stand in one place',
      transit: 'UIC-Halsted (Blue)',
      why: 'Quiet, so your runner will actually see and hear you when they need it.',
      downside: 'Less atmosphere — this is where the crowds thin out.',
    },
    {
      mile: 19.25,
      name: 'Pilsen · 18th Street',
      where: '18th St at Loomis',
      transit: '18th (Pink), then four blocks east',
      why: 'Mariachi bands and a huge community party right where runners hit the wall.',
      downside: 'The Pink Line runs less often than the Red Line, so leave time.',
    },
    {
      mile: 21.5,
      name: 'Chinatown · Cermak & Wentworth',
      where: 'Wentworth Ave & Cermak Rd — stand on the east side',
      transit: 'Cermak-Chinatown (Red)',
      why: 'Lion dancers, loud and colourful, and easy by Red Line. You can walk to mile 25 from here.',
      downside: 'Busy, and the stretch is short.',
    },
    {
      mile: 23,
      name: 'Bronzeville · 35th St',
      where: 'Michigan Ave / Indiana Ave at 35th St',
      transit: '35th-Bronzeville-IIT (Green) or Sox-35th (Red)',
      why: 'Sparse crowds about 5 km from the finish — one voice makes a difference here.',
      downside: 'Little shade and few spectators, so not a party.',
    },
    {
      mile: 25,
      name: 'South Michigan · Cermak',
      where: 'Michigan Ave & Cermak Rd — stand on the west side',
      transit: 'Cermak-McCormick Place (Green), or walk ~10 min from Chinatown',
      why: 'A clear view of the finishing push without the Roosevelt crowds.',
    },
    {
      mile: 26,
      name: '“Mount Roosevelt”',
      where: 'Michigan Ave & Roosevelt Rd',
      transit: 'Roosevelt (Red/Green/Orange), then walk east',
      why: 'The course’s only real climb — the bridge over Roosevelt Rd, about 500 m from the line. Runners need the noise.',
      downside: 'The most crowded spot; hard to see your runner.',
      cheerZone: 'Bank of America Cheer Zone',
    },
  ],
  routes: [
    {
      title: 'Four sightings, no course crossings',
      steps: [
        'Mile 3.5 — Chicago & LaSalle, east side. Walk 5 min to Chicago (Red).',
        'Red Line to North/Clybourn, walk ~12 min to mile 11 at North & Sedgwick, west side.',
        'Back to North/Clybourn, Red Line ~15 min south to Cermak-Chinatown for mile 21.5, east side.',
        'Walk ~10 min to mile 25 at Michigan & Cermak, west side.',
        'Red Line north to Jackson or Monroe and walk to Runner Reunite.',
      ],
      note: 'Four sightings without crossing the course (adapted from CITIUS Mag). For a sub-3:00 runner, drop a stop — mile 21.5 to 25 is tight.',
    },
    {
      title: 'Party route',
      steps: [
        'Mile 1 on Grand Ave for the elites.',
        'Red Line Grand → Addison, walk east to mile 8.5 in Northalsted.',
        'Red or Brown Line back to the Loop for mile 13 on Wacker.',
        'Red Line to Sox-35th for mile 23.',
        'Meet near Michigan Ave.',
      ],
      note: 'A local’s route from Axios (2022). Check miles 1–2 against the 2026 map first.',
    },
    {
      title: 'Northside double, low effort',
      steps: [
        'Sheridan & Belmont (mile 7), then walk 6 min to Broadway & Belmont (mile 9).',
        'Red Line to Cermak-Chinatown for mile 21.5.',
        'Walk to Michigan & Cermak for mile 25.',
      ],
      note: 'One course crossing at mile 9.',
    },
  ],
  rules: [
    {
      label: 'Finish line',
      text: 'Only with a Finish Line Viewing ticket: US$30, kids 8 and under free, 8:30 am–4 pm, enter at Michigan Ave & 11th St (wristband, re-entry OK). Our race-weekend research found it sold out, so check before planning on it.',
    },
    {
      label: 'Reunite',
      text: 'Butler Field inside the 27th Mile party, from 9:30 am, via Gate 1 (Jackson) or Gate 4 (Ida B. Wells). Lettered pillars — pick a letter in advance. Allow 30+ min for the runner to get there.',
    },
    {
      label: 'Tracking',
      text: 'Free official app (powered by TCS): unlimited live tracking, course map and live stream. Download before race day.',
    },
    {
      label: 'Brand zones',
      text: 'Endorphins mile 11 · STORM mile 13 · lululemon mile 16 · 3RUN2 mile 20 · Heartbreak mile 20.5 · Shokz mile 22 (Wentworth & 26th).',
    },
    {
      label: 'Banned',
      text: 'Backpacks and large bags, hard coolers, chairs, drones, pets, alcohol, full-face costumes, bikes, strollers, scooters. Police do random bag checks.',
    },
    {
      label: 'Etiquette',
      text: 'Stay off the road (it’s curb to curb), stand on the side that leads to your next train, no chalk or confetti cannons, and don’t hand runners food or drink.',
    },
    {
      label: 'Transit',
      text: 'Course streets close 6 am–4 pm and 30+ bus routes reroute — use the ‘L’. A Ventra day/weekend pass or a tapped contactless card covers it.',
    },
  ],
  sources: [
    {
      title: 'Chicago Marathon — spectator information',
      url: 'https://www.chicagomarathon.com/event-info/spectator-info/',
      summary: 'Official 2026 schedule, cheer zones, access rules and the tracking app.',
    },
    {
      title: 'Chicago Marathon — navigating the course',
      url: 'https://www.chicagomarathon.com/spectators/navigating-the-course/',
      summary: 'Official mile-by-mile L stations.',
    },
    {
      title: 'NBC Chicago — 2026 transportation guide',
      url: 'https://www.nbcchicago.com/news/local/a-2026-chicago-marathon-transportation-guide-how-to-get-around-on-race-day/3964248/',
      summary: 'Which station for which mile in 2026.',
    },
    {
      title: 'CITIUS Mag — spectator guide (2024)',
      url: 'https://citiusmag.com/articles/getting-chicked-chicago-marathon-spectator-guide',
      summary: 'No-crossing multi-stop routes and which side of the street to stand on.',
    },
    {
      title: 'The Running Channel — 2026 guide',
      url: 'https://therunningchannel.com/ultimate-guide-to-the-chicago-marathon/',
      summary: 'Reports the 2026 Dearborn-for-State change between miles 1 and 2.',
    },
    {
      title: 'Chicago Marathon — Finish Line Viewing',
      url: 'https://www.chicagomarathon.com/spectators/finish-line-viewing/',
      summary: 'US$30 ticket, 8:30 am–4 pm, entry at Michigan & 11th.',
    },
  ],
};
